import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import type { CSSProperties } from 'react';
import EmployeeDetailsPopover from './EmployeeDetailsPopover.tsx';
import type { DoctorSupervision, AssignedRoomInfo, SiteLockControl, RoomTransferControl, SiteCoinOption, DoctorStatusControl, AssignReliefControl } from './EmployeeDetailsPopover.tsx';
import { NAMEPLATE_COLORS } from './staffNameplates.ts';
import type { Employee } from '../services/api.ts';

// A candidate the user can pick to fill an empty slot (anesthesiologists with an open signature,
// available CRNAs, available students).
export interface SlotCandidate { id: string; label: string; hint?: string }

// A single staff occupant for a nameplate slot.
export interface Plate {
  name: string;      // shown in the box (shrinks to fit)
  color: string;     // fill
  title?: string;    // hover text (full name)
  textColor?: string;
  // When present, clicking/hovering the plate opens the person card.
  employee?: Employee;
  shift?: { startTimeUtc: string; endTimeUtc: string; order?: number };
  hospitalName?: string;
  supervision?: DoctorSupervision;
  assignedRooms?: AssignedRoomInfo[];
  reportInUtc?: string;   // derived report-in for the card: no lead for a room they take over
  recolor?: { current?: string; onPick: (color: string | null) => void };
  // Real current site names — filters the person-card "Credentialed sites" to sites that actually exist.
  validSiteNames?: string[];
  // True when this plate is an "assumed continuation" of an earlier case's team (shown green).
  // Inherited plates aren't a real allocation on THIS case, so they can't be dragged off.
  inherited?: boolean;
  // True when this doctor has a meeting today (set on the Anesthesiologists tab) — shows a brown corner tab.
  meeting?: boolean;
  // True when this doctor has a request case today (disposition) — draws a ring around the plate (a
  // border cue like the nerve-block / pediatric case borders), leaving the shift fill visible.
  request?: boolean;
  // Editable site "coin" (hard lock) + room-transfer control shown in the person card.
  siteLock?: SiteLockControl;
  roomTransfer?: RoomTransferControl;
  // Doctor-card extras: the site coin(s) they're at (next to the name), colored credential coins, and the
  // Meeting / Request case / Off disposition control (shared per-day store with the Anesthesiologists tab).
  atSites?: SiteCoinOption[];
  credentialSites?: SiteCoinOption[];
  statusControl?: DoctorStatusControl;
  // Board only: "Assign relief" control (a bullpen-style picker + the current relief) shown in the person card.
  assignRelief?: AssignReliefControl;
  // True when this provider OPENS the room with less than the report-in lead (30 min, 45 for a nerve block) — a float
  // needs to go help them set up, so the name block is outlined in red (boardViewHelpers.shortPrepMinutes).
  shortPrep?: boolean;
  // True when this provider is NOT credentialed at this room's site. Symphony never places anyone
  // uncredentialed — it leaves the room unstaffed instead — so this only fires for a hand placement or a row
  // that predates a credential change. Braden 2026-09-30: "there needs to be a red outline on their nameplate
  // tag so that we can show hey you have enough people for everything today but these people need to be
  // reviewed or swapped out because they don't have credentials here."
  uncredentialed?: boolean;
}

// Brown "has a meeting today" corner tab (matches the Anesthesiologists-tab meeting color).
const MEETING_COLOR = '#4a2c10';
// Red outline on a short-prep opener's name block. Drawn INSIDE the block so the row layout never shifts.
const SHORT_PREP_OUTLINE: CSSProperties = { outline: '2px solid #dc2626', outlineOffset: '-2px' };
// Red DASHED outline for a provider who is not credentialed at this site — same alarm colour as a short-prep
// opener but a different stroke, so the two never read as the same problem. Also drawn inside the block.
const UNCREDENTIALED_OUTLINE: CSSProperties = { outline: '2px dashed #dc2626', outlineOffset: '-2px' };
// Bottom-right "!" so the flag survives a plate too narrow to show its outline clearly. Shared by the room's own
// plates and the relief plates above them, so every provider in the room is flagged the same way.
const UncredentialedMark: React.FC = () => (
  <span aria-label="not credentialed at this site" title="Not credentialed at this site — review or swap"
    style={{ position: 'absolute', right: '2px', bottom: 0, fontSize: '8px', fontWeight: 900, lineHeight: 1, color: '#dc2626', textShadow: '0 0 2px #fff' }}>!</span>
);

// "Request case" ring. A BORDER-style cue like the nerve-block / pediatric case borders: it draws over
// the plate without replacing its fill, so the provider keeps their shift colour.
//
// An inset box-shadow rather than an `outline` on purpose. This plate already composes outline THREE
// ways (SHORT_PREP_OUTLINE, UNCREDENTIALED_OUTLINE, and the drag-over overStyle) and `outline` is a
// single property, so a fourth user would silently beat or lose to the red credential/short-prep
// alarms. box-shadow stacks independently, so a provider who is both uncredentialed AND has a request
// shows both cues. SLOT_BASE sets no boxShadow, so nothing is clobbered, and its borderRadius: 5px
// means the ring follows the plate's corners.
const requestRing = (color: string): CSSProperties => ({ boxShadow: `inset 0 0 0 2px ${color}` });

// The "when" label for a plate's hover tip: a pecking-order worker shows "Pecking #N"; everyone
// else shows their shift window (viewer-local time), e.g. "5:30 AM – 2:00 PM".
const whenLabel = (plate: Plate): string => {
  const s = plate.shift;
  const t = (utc: string) => new Date(utc).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const base = !s ? (plate.title || plate.name) : (s.order !== undefined ? `Pecking #${s.order}` : `${t(s.startTimeUtc)} – ${t(s.endTimeUtc)}`);
  if (plate.uncredentialed) return `${base} · NOT CREDENTIALED at this site — review or swap`;
  return plate.shortPrep ? `${base} · short prep, needs help setting up` : base;
};

// Signature dots for a doctor's hover tip (mirrors the person-card scorecard): one dot per
// signature slot (cap = max concurrent CRNA coverage, default 4). Filled = a room they're on;
// empty (recessed) = an OPEN signature. A solo doctor has no signatures to give -> "in room".
const SigDots: React.FC<{ employee: Employee; supervision: DoctorSupervision }> = ({ employee, supervision }) => {
  const cap = employee.maxConcurrentNurseCoverageRatio ?? 4;
  const supervised = supervision.crnaRooms + supervision.greenStudentRooms + supervision.otherStudentRooms;
  const isSolo = supervision.soloRooms > 0 && supervised === 0;
  const used = supervision.signaturesUsed ?? supervision.concurrentRooms ?? supervision.totalRooms;
  const filled = Math.min(used, cap);
  const open = Math.max(0, cap - used);
  if (isSolo) return <span style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)' }}>In room (solo)</span>;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      {Array.from({ length: cap }).map((_, i) => (
        <span key={i} aria-hidden style={{
          width: '9px', height: '9px', borderRadius: '50%', flexShrink: 0,
          background: i < filled ? 'var(--amber)' : 'var(--bg-input)',
          border: i < filled ? '1px solid rgba(120,60,0,0.55)' : '1px solid var(--border-soft)',
          boxShadow: i < filled ? '0 1px 2px rgba(0,0,0,0.30)' : 'inset 0 1px 2px rgba(0,0,0,0.45)',
          opacity: i < filled ? 1 : 0.7,
        }} />
      ))}
      <span style={{ marginLeft: '3px', fontSize: '11px', fontWeight: 700, color: open > 0 ? 'var(--amber)' : 'var(--text-faint)' }}>{open} open</span>
    </span>
  );
};

// Hover-tip CONTENT for a plate: a DOCTOR shows signature dots (how many are still open) above
// their shift window / pecking-order number; CRNAs and students show just that when-label.
const plateTip = (plate: Plate): React.ReactNode => {
  const when = whenLabel(plate);
  if (plate.employee?.employeeType === 'doctor' && plate.supervision) {
    return (
      <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
        <SigDots employee={plate.employee} supervision={plate.supervision} />
        <span>{when}</span>
      </span>
    );
  }
  return when;
};

// INSTANT hover tooltip (no ~1s browser-title delay) showing `content` right below the plate.
// Returns mouse handlers to spread on the plate div plus a portaled tooltip node to render
// alongside it. Portaled to <body> so the board's overflow:hidden can't clip it; pointerEvents
// 'none' so it never blocks a drag/drop.
const useHoverTip = (content: React.ReactNode): { handlers: React.DOMAttributes<HTMLElement>; tip: React.ReactNode } => {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const active = content !== null && content !== undefined && content !== '';
  const handlers: React.DOMAttributes<HTMLElement> = active ? {
    onMouseEnter: (e: React.MouseEvent<HTMLElement>) => {
      const r = e.currentTarget.getBoundingClientRect();
      setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
    },
    onMouseLeave: () => setPos(null),
  } : {};
  const tip = (active && pos) ? ReactDOM.createPortal(
    <div style={{
      position: 'fixed', left: `${pos.x}px`, top: `${pos.y}px`, transform: 'translateX(-50%)', zIndex: 100001,
      background: 'var(--popover-bg)', color: 'var(--text-primary)', border: '1px solid var(--popover-border)',
      borderRadius: '6px', padding: '5px 9px', fontSize: '12px', fontWeight: 700, whiteSpace: 'nowrap',
      boxShadow: 'var(--shadow-lg)', pointerEvents: 'none', textAlign: 'center',
    }}>{content}</div>, document.body) : null;
  return { handlers, tip };
};

// A doctor "coin" for a hospital-section header: the small nameplate-colored pill that mirrors the
// doctor's board nameplate. Hovering it shows the SAME signature scorecard as the nameplate — how
// many supervision signatures are still open — above the doctor's name/status. Lives next to
// SigDots/useHoverTip so the coin's hover can never drift from the nameplate's.
export const DoctorCoin: React.FC<{
  label: string;                     // last name shown on the coin
  color: string;                     // nameplate color
  name: string;                      // full name, shown in the hover tip
  status?: string;                   // e.g. "Supervising CRNA(s)" / "Solo in a room"
  employee?: Employee;               // supplies the signature cap
  supervision?: DoctorSupervision;   // supplies the used/open signature math
}> = ({ label, color, name, status, employee, supervision }) => {
  const showSig = employee?.employeeType === 'doctor' && !!supervision;
  const content = (
    <span style={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
      <span>{status ? `${name} — ${status}` : name}</span>
      {showSig && <SigDots employee={employee!} supervision={supervision!} />}
    </span>
  );
  const { handlers, tip } = useHoverTip(content);
  return (
    <span
      {...handlers}
      style={{
        fontSize: '0.6em', fontWeight: 700, letterSpacing: '0.2px', padding: '2px 9px', borderRadius: '999px',
        background: color, color: '#fff', whiteSpace: 'nowrap', textShadow: '0 1px 1px rgba(0,0,0,0.35)',
        border: '1px solid rgba(255,255,255,0.4)', cursor: 'default',
      }}
    >
      {label}
      {tip}
    </span>
  );
};

// Shrinks its text's font size until it fits the box width (down to a floor).
export const FitText: React.FC<{ text: string; max?: number; min?: number }> = ({ text, max = 12, min = 7 }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(max);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let s = max;
    const fits = () => { el.style.fontSize = `${s}px`; return el.scrollWidth <= el.clientWidth + 1; };
    let guard = 0;
    while (s > min && !fits() && guard < 40) { s -= 0.5; guard++; }
    setFontSize(s);
  }, [text, max, min]);
  return (
    <span ref={ref} style={{ display: 'block', width: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textAlign: 'center', fontSize: `${fontSize}px` }}>
      {text}
    </span>
  );
};

const SLOT_BASE: CSSProperties = {
  height: '20px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center',
  padding: '0 5px', boxSizing: 'border-box', overflow: 'hidden', minWidth: 0,
};
// Empty slot: a recessed, faded "slot" that clearly wants something dropped into it (solid,
// inset shadow + gradient rather than a dotted outline).
const EMPTY_SLOT: CSSProperties = {
  ...SLOT_BASE,
  border: '1px solid var(--border-soft)',
  background: 'linear-gradient(180deg, var(--bg-input), var(--bg-surface-raised))',
  boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.30)',
  opacity: 0.7,
};

export type SlotKind = 'doctor' | 'crna' | 'student';
type ColumnKind = 'anesthesiologist' | 'CRNA' | 'student';
const KIND_ACCEPTS: Record<ColumnKind, (t: string) => boolean> = {
  anesthesiologist: (t) => t === 'doctor',
  CRNA: (t) => t === 'nurse',
  student: (t) => t.startsWith('nurse-student'),
};
const KIND_TO_SLOT: Record<ColumnKind, SlotKind> = { anesthesiologist: 'doctor', CRNA: 'crna', student: 'student' };
// Synthetic picker options (doctor/CRNA slots) that set the empty-slot prompt by hand instead of picking a person.
const MARK_NEED = '__mark_need__';
const MARK_NONE = '__mark_none__';
const MARK_CLEAR = '__mark_clear__';

const Slot: React.FC<{
  plate?: Plate;
  kind: ColumnKind;
  needStaff?: boolean;   // empty slot for a REQUIRED role that's missing -> red "Need <role>" prompt
  // Nobody working today is CREDENTIALED for this room, so no one could ever have filled it. Shows NO CRED
  // instead of NEED MD/NEED CRNA: a credentialing problem to fix, not a provider to go find (Braden 2026-09-18).
  noCred?: boolean;
  scheduleId?: string;
  onDropStaff?: (scheduleId: string, slotKind: SlotKind, employeeId: string) => void;
  onRemoveStaff?: (scheduleId: string, employeeId: string) => void;
  // A doctor OR CRNA dropped onto an OCCUPIED CRNA slot -> they relieve that CRNA (a relief plate above the
  // CRNA; a doctor reliever goes in solo/orange). Handled by the parent; the CRNA is NOT removed here.
  onDoctorOnCrna?: (scheduleId: string, relieverId: string) => void;
  // A doctor dropped onto an OCCUPIED anesthesiologist slot -> they relieve that doctor (relief plate above
  // the doctor). Doc-on-doc only. Handled by the parent; the doctor is NOT removed here.
  onReliefOnDoctor?: (scheduleId: string, doctorId: string) => void;
  // Candidates to offer when an EMPTY slot is clicked (lazy — called on click).
  getCandidates?: (slotKind: SlotKind) => SlotCandidate[];
  // Manual empty-slot marks (doctor/CRNA slots). noneMarked renders a muted "NONE" brick (matching the
  // empty-slot background) in place of the red NEED prompt. The picker offers Need / None / Clear options
  // (via onSetMark) so the runner can drive the prompt by hand. hasMark enables the "Clear" option.
  noneMarked?: boolean;
  hasMark?: boolean;
  onSetMark?: (mark: 'need' | 'none' | null) => void;
}> = ({ plate, kind, needStaff, noCred, scheduleId, onDropStaff, onRemoveStaff, onDoctorOnCrna, onReliefOnDoctor, getCandidates, noneMarked, hasMark, onSetMark }) => {
  const [over, setOver] = useState(false);
  const [menu, setMenu] = useState<{ top: number; left: number; items: SlotCandidate[] } | null>(null);
  const shiftTip = useHoverTip(plate ? plateTip(plate) : null); // instant hover tip (hook must run before any early return)
  const emptyRef = useRef<HTMLDivElement>(null);
  // Close the picker on any outside click / scroll / escape.
  useEffect(() => {
    if (!menu) return;
    const close = () => setMenu(null);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setMenu(null); };
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', close, true);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('scroll', close, true); document.removeEventListener('keydown', onKey); };
  }, [menu]);
  const canMark = !!onSetMark && (kind === 'anesthesiologist' || kind === 'CRNA');
  const openPicker = () => {
    if (!scheduleId || !onDropStaff || !getCandidates) return;
    let items = getCandidates(KIND_TO_SLOT[kind]);
    // Doctor / CRNA slots: let the runner set the empty-slot prompt by hand — flag it required (Need),
    // mark it intentionally empty (None), or clear a manual mark — above the real candidates. Picking a
    // real person below still fills the slot as normal.
    if (canMark) {
      const roleWord = kind === 'CRNA' ? 'a CRNA' : 'a doctor';
      const noneHint = kind === 'CRNA' ? 'doctor-only, no CRNA' : 'no doctor needed';
      const synth: SlotCandidate[] = [];
      if (!needStaff) synth.push({ id: MARK_NEED, label: `Need ${roleWord}`, hint: 'flag as required' });
      if (!noneMarked) synth.push({ id: MARK_NONE, label: 'None', hint: noneHint });
      if (hasMark) synth.push({ id: MARK_CLEAR, label: 'Clear', hint: 'blank slot' });
      items = [...synth, ...items];
    }
    const r = emptyRef.current?.getBoundingClientRect();
    setMenu({ top: (r?.bottom ?? 0) + 4, left: r?.left ?? 0, items });
  };
  const canDrop = !!(scheduleId && onDropStaff);

  const dnd = canDrop ? {
    onDragOver: (e: React.DragEvent) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; },
    onDragEnter: (e: React.DragEvent) => { e.preventDefault(); setOver(true); },
    onDragLeave: () => setOver(false),
    onDrop: (e: React.DragEvent) => {
      setOver(false);
      e.preventDefault();
      const raw = e.dataTransfer.getData('application/x-staff');
      if (!raw) return;
      try {
        const { employeeId, employeeType } = JSON.parse(raw);
        // Drop onto an OCCUPIED slot = RELIEVE the person there (a relief plate appears above them; the person
        // is NOT removed). A doctor OR CRNA can relieve a CRNA; only a doctor can relieve a doctor (doc-on-doc).
        if (kind === 'CRNA' && plate && (employeeType === 'doctor' || employeeType === 'nurse') && onDoctorOnCrna) {
          onDoctorOnCrna(scheduleId!, employeeId);
          return;
        }
        if (kind === 'anesthesiologist' && plate && employeeType === 'doctor' && onReliefOnDoctor) {
          onReliefOnDoctor(scheduleId!, employeeId);
          return;
        }
        if (!KIND_ACCEPTS[kind](employeeType)) return; // brick came from the wrong column
        onDropStaff!(scheduleId!, KIND_TO_SLOT[kind], employeeId);
      } catch { /* ignore malformed payloads */ }
    },
  } : {};
  const overStyle: CSSProperties = over ? { outline: '2px solid var(--or-blue-mid)', outlineOffset: '-1px', opacity: 1 } : {};

  if (!plate) {
    const label = kind === 'anesthesiologist' ? 'an anesthesiologist' : `a ${kind}`;
    const clickable = !!(scheduleId && onDropStaff && getCandidates);
    // A REQUIRED-but-missing slot (e.g. a supervised room with no CRNA) shows a red "Need <role>" prompt,
    // while staying a drop target so a CRNA can still be dropped/chosen to fill it. A room the runner marked
    // doctor-only shows a MUTED "NONE" brick that blends into the empty-slot background. In both states the
    // slot is CLICKABLE — it opens the picker (the CRNA picker carries a "None" / "Needs a CRNA" option).
    const warnStyle: CSSProperties = noneMarked
      ? { color: 'var(--text-primary)', opacity: 0.45, fontWeight: 700, letterSpacing: '0.5px' }
      : needStaff
      ? { border: '1px solid #dc2626', background: 'rgba(220,38,38,0.14)', boxShadow: 'none', opacity: 1, color: '#dc2626', fontWeight: 700 }
      : {};
    const emptyTitle = noneMarked
      ? `Marked no ${kind === 'CRNA' ? 'CRNA' : 'doctor'} — click to change, or drop one to fill`
      : needStaff
      ? (noCred
        ? `Nobody working today is credentialed for this room — fix credentials, or drop someone in anyway`
        : `Needs ${label} — drop one here${clickable ? `, or click to choose${canMark ? ' / None' : ''}` : ''}`)
      : (clickable ? `Drop ${label} here, or click to choose${canMark ? ' (or flag Need / None)' : ''}` : `Drop ${label} here`);
    return (
      <>
        <div
          ref={emptyRef}
          style={{ ...EMPTY_SLOT, ...warnStyle, ...overStyle, cursor: clickable ? 'pointer' : undefined }}
          title={emptyTitle}
          aria-label={noneMarked ? `${kind} not needed` : needStaff ? (noCred ? `no credentialed ${kind}` : `needs ${kind}`) : `empty ${kind} slot`}
          onClick={clickable ? openPicker : undefined}
          {...dnd}
        >
          {noneMarked
            ? <FitText text="NONE" max={9} min={6} />
            : needStaff && <FitText text={noCred ? 'NO CRED' : kind === 'CRNA' ? 'NEED CRNA' : kind === 'anesthesiologist' ? 'NEED MD' : 'NEED STUDENT'} max={9} min={6} />}
        </div>
        {menu && ReactDOM.createPortal(
          <div
            onMouseDown={(e) => e.stopPropagation()}
            style={{ position: 'fixed', top: `${menu.top}px`, left: `${menu.left}px`, zIndex: 100001, minWidth: '190px', maxHeight: '260px', overflowY: 'auto',
              background: 'var(--popover-bg)', border: '1px solid var(--popover-border)', borderRadius: '8px', boxShadow: 'var(--shadow-lg)', padding: '4px' }}
          >
            <div style={{ fontSize: '0.66em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', color: 'var(--text-faint)', padding: '4px 8px' }}>
              {kind === 'anesthesiologist' ? 'Anesthesiologists with an open signature' : kind === 'CRNA' ? 'Available CRNAs' : 'Available students'}
            </div>
            {menu.items.length === 0 ? (
              <div style={{ fontSize: '0.78em', color: 'var(--text-faint)', padding: '6px 8px' }}>None available.</div>
            ) : menu.items.map(it => (
              <button key={it.id}
                onClick={() => {
                  if (it.id === MARK_NEED) onSetMark?.('need');
                  else if (it.id === MARK_NONE) onSetMark?.('none');
                  else if (it.id === MARK_CLEAR) onSetMark?.(null);
                  else onDropStaff!(scheduleId!, KIND_TO_SLOT[kind], it.id);
                  setMenu(null);
                }}
                style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', width: '100%', textAlign: 'left', border: 'none', background: 'transparent', color: 'var(--text-primary)', padding: '5px 8px', borderRadius: '5px', cursor: 'pointer', fontSize: '0.82em' }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg-surface-raised)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <span style={{ fontWeight: 600 }}>{it.label}</span>
                {it.hint && <span style={{ fontSize: '0.82em', color: 'var(--amber)', fontWeight: 700, whiteSpace: 'nowrap' }}>{it.hint}</span>}
              </button>
            ))}
          </div>, document.body)}
      </>
    );
  }
  // A filled plate is draggable so it can be dragged OFF the room (drop on the bullpen to remove)
  // or onto another room. Payload carries the source room so a drop-on-bullpen knows what to remove.
  // Inherited (assumed-continuation) plates aren't a real allocation here, so they aren't draggable.
  const dragOff = plate.employee && scheduleId && !plate.inherited ? {
    draggable: true,
    onDragStart: (ev: React.DragEvent) => {
      ev.dataTransfer.setData('application/x-staff', JSON.stringify({
        employeeId: plate.employee!.employeeId, employeeType: plate.employee!.employeeType, fromScheduleId: scheduleId,
      }));
      // copyMove so a drop on ANOTHER slot (which sets dropEffect 'copy') registers as a real move/add;
      // only a drop in truly empty space reports 'none' -> the drag-off-to-remove path.
      ev.dataTransfer.effectAllowed = 'copyMove';
    },
    // Dropped in EMPTY SPACE (no valid slot/bullpen took it) -> remove from this room. A CRNA / solo
    // anesthesiologist frees up entirely (back to the bullpen); a supervising anesthesiologist just
    // vacates THIS room (their other rooms stay, one signature drops). handleRemoveStaff decides which.
    onDragEnd: (ev: React.DragEvent) => {
      if (ev.dataTransfer.dropEffect === 'none' && onRemoveStaff && scheduleId && plate.employee) {
        onRemoveStaff(scheduleId, plate.employee.employeeId);
      }
    },
  } : {};
  const canDragOff = !!(plate.employee && scheduleId && !plate.inherited);
  const filled = (
    <>
      <div data-short-prep={plate.shortPrep ? 'true' : undefined} data-uncredentialed={plate.uncredentialed ? 'true' : undefined} style={{ ...SLOT_BASE, position: 'relative', background: plate.color, color: plate.textColor || '#fff', fontWeight: 600, cursor: canDragOff ? 'grab' : (plate.employee ? 'pointer' : undefined), ...(plate.request ? requestRing(NAMEPLATE_COLORS.request) : {}), ...(plate.shortPrep ? SHORT_PREP_OUTLINE : {}), ...(plate.uncredentialed ? UNCREDENTIALED_OUTLINE : {}), ...overStyle }} {...shiftTip.handlers} {...dnd} {...dragOff}>
        <FitText text={plate.name} />
        {plate.uncredentialed && <UncredentialedMark />}
        {plate.meeting && (
          // Brown tag down the LEFT EDGE of the brick = meeting today.
          <span aria-label="meeting today" title="Meeting today" style={{ position: 'absolute', top: 0, left: 0, bottom: 0, width: '4px', background: MEETING_COLOR, borderTopLeftRadius: '5px', borderBottomLeftRadius: '5px' }} />
        )}
        {plate.request && (
          // Screen-reader / hover affordance only -- the visible cue is the requestRing on the plate itself.
          <span aria-label="request case" title="Request case" style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }} />
        )}
      </div>
      {shiftTip.tip}
    </>
  );
  // Click/hover the filled plate to open the person card.
  return plate.employee ? (
    <EmployeeDetailsPopover employee={plate.employee} shift={plate.shift} hospitalName={plate.hospitalName} supervision={plate.supervision} assignedRooms={plate.assignedRooms} reportInUtc={plate.reportInUtc} recolor={plate.recolor} siteLock={plate.siteLock} roomTransfer={plate.roomTransfer} validSiteNames={plate.validSiteNames} atSites={plate.atSites} credentialSites={plate.credentialSites} statusControl={plate.statusControl} boardActivityCoin={plate.employee?.employeeType === 'doctor' || plate.employee?.employeeType === 'nurse'} assignRelief={plate.assignRelief}
      triggerStyle={{ display: 'block', width: '100%', textDecoration: 'none' }}>
      {filled}
    </EmployeeDetailsPopover>
  ) : filled;
};

// A small "relief planning" plate for the upper row — draggable OFF to cancel (drop in empty space).
const ReliefPlate: React.FC<{ plate: Plate; scheduleId: string; onClear: (scheduleId: string) => void }> = ({ plate, scheduleId, onClear }) => {
  const shiftTip = useHoverTip(plateTip(plate));
  const inner = (
    <>
      <div
        draggable
        onDragStart={(ev) => {
          ev.dataTransfer.setData('application/x-staff', JSON.stringify({ employeeId: plate.employee?.employeeId, employeeType: plate.employee?.employeeType || 'doctor', fromScheduleId: scheduleId }));
          ev.dataTransfer.effectAllowed = 'copyMove';
        }}
        onDragEnd={(ev) => { if (ev.dataTransfer.dropEffect === 'none') onClear(scheduleId); }}
        data-uncredentialed={plate.uncredentialed ? 'true' : undefined}
        style={{ ...SLOT_BASE, position: 'relative', background: plate.color, color: plate.textColor || '#fff', fontWeight: 600, cursor: 'grab', ...(plate.uncredentialed ? UNCREDENTIALED_OUTLINE : {}) }}
        {...shiftTip.handlers}
      >
        <FitText text={plate.name} />
        {plate.uncredentialed && <UncredentialedMark />}
      </div>
      {shiftTip.tip}
    </>
  );
  return plate.employee ? (
    <EmployeeDetailsPopover employee={plate.employee} shift={plate.shift} hospitalName={plate.hospitalName} supervision={plate.supervision} assignedRooms={plate.assignedRooms} reportInUtc={plate.reportInUtc} siteLock={plate.siteLock} roomTransfer={plate.roomTransfer} validSiteNames={plate.validSiteNames} atSites={plate.atSites} credentialSites={plate.credentialSites} statusControl={plate.statusControl} boardActivityCoin={plate.employee?.employeeType === 'doctor' || plate.employee?.employeeType === 'nurse'} assignRelief={plate.assignRelief}
      triggerStyle={{ display: 'block', width: '100%', textDecoration: 'none' }}>
      {inner}
    </EmployeeDetailsPopover>
  ) : inner;
};

// An empty "relief supervisor needed" drop box (above a supervisor who has gone in-room). Accepts a doctor.
const ReliefDropBox: React.FC<{ scheduleId: string; onDrop: (scheduleId: string, doctorId: string) => void }> = ({ scheduleId, onDrop }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'copy'; }}
      onDragEnter={(e) => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={(e) => {
        setOver(false); e.preventDefault();
        const raw = e.dataTransfer.getData('application/x-staff'); if (!raw) return;
        try { const { employeeId, employeeType } = JSON.parse(raw); if (employeeType === 'doctor') onDrop(scheduleId, employeeId); } catch { /* ignore */ }
      }}
      title="Drop a relief supervisor here"
      aria-label="relief supervisor needed"
      style={{ ...SLOT_BASE, border: '1px dashed #d9a406', background: 'rgba(217,164,6,0.10)', color: '#a9791f', fontSize: '8px', fontWeight: 700, letterSpacing: '0.3px', opacity: over ? 1 : 0.85, outline: over ? '2px solid #d9a406' : 'none' }}
    >
      RELIEF
    </div>
  );
};

// Three equal-width nameplates (Anesthesiologist | CRNA | Student) spanning the cell width.
// An optional UPPER row overlays relief-planning boxes aligned to the same columns:
//   • above the anesthesiologist: a relief supervisor (or a blank "RELIEF" drop box when needed)
//   • above the CRNA: an anesthesiologist who has gone into the room to relieve them (orange).
// Empty main slots render as recessed drop targets; every slot accepts a matching bullpen brick.
const RoomNamePlates: React.FC<{
  doctor?: Plate; crna?: Plate; student?: Plate;
  needDoctor?: boolean;            // in-scope room with an empty doctor slot -> red "NEED MD" prompt
  needCrna?: boolean;              // room has a doctor but no CRNA -> "NEED CRNA" prompt in the CRNA slot
  // Symphony reported that nobody working is credentialed for this room -> the prompt reads NO CRED instead.
  doctorNoCred?: boolean;
  crnaNoCred?: boolean;
  inRoomDoctor?: Plate;            // above the CRNA: doctor gone in to relieve (doc-in-room color)
  reliefSupervisor?: Plate;        // above the supervisor: the assigned relief supervisor
  needsReliefSupervisor?: boolean; // above the supervisor: show a blank RELIEF drop box
  scheduleId?: string;
  onDropStaff?: (scheduleId: string, slotKind: SlotKind, employeeId: string) => void;
  onRemoveStaff?: (scheduleId: string, employeeId: string) => void;
  onDoctorOnCrna?: (scheduleId: string, doctorId: string) => void;
  onDropReliefSupervisor?: (scheduleId: string, doctorId: string) => void;
  onClearInRoom?: (scheduleId: string) => void;
  onClearReliefSupervisor?: (scheduleId: string) => void;
  getCandidates?: (slotKind: SlotKind) => SlotCandidate[];
  // Manual empty-slot marks (muted "NONE" / hand-flagged "NEED") for the doctor and CRNA slots.
  doctorNone?: boolean; doctorHasMark?: boolean; onSetDoctorMark?: (mark: 'need' | 'none' | null) => void;
  crnaNone?: boolean; crnaHasMark?: boolean; onSetCrnaMark?: (mark: 'need' | 'none' | null) => void;
}> = ({ doctor, crna, student, needDoctor, needCrna, doctorNoCred, crnaNoCred, inRoomDoctor, reliefSupervisor, needsReliefSupervisor, scheduleId, onDropStaff, onRemoveStaff, onDoctorOnCrna, onDropReliefSupervisor, onClearInRoom, onClearReliefSupervisor, getCandidates, doctorNone, doctorHasMark, onSetDoctorMark, crnaNone, crnaHasMark, onSetCrnaMark }) => {
  const showUpper = !!(inRoomDoctor || reliefSupervisor || needsReliefSupervisor);
  const Spacer = () => <div />;
  return (
    <div style={{ marginTop: '3px', width: '100%' }}>
      {showUpper && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px', marginBottom: '3px', width: '100%' }}>
          {/* above the anesthesiologist */}
          {reliefSupervisor && scheduleId && onClearReliefSupervisor
            ? <ReliefPlate plate={reliefSupervisor} scheduleId={scheduleId} onClear={onClearReliefSupervisor} />
            : (needsReliefSupervisor && scheduleId && onDropReliefSupervisor ? <ReliefDropBox scheduleId={scheduleId} onDrop={onDropReliefSupervisor} /> : <Spacer />)}
          {/* above the CRNA */}
          {inRoomDoctor && scheduleId && onClearInRoom
            ? <ReliefPlate plate={inRoomDoctor} scheduleId={scheduleId} onClear={onClearInRoom} />
            : <Spacer />}
          <Spacer />
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '3px', width: '100%' }}>
        <Slot plate={doctor} kind="anesthesiologist" needStaff={needDoctor} noCred={doctorNoCred} scheduleId={scheduleId} onDropStaff={onDropStaff} onRemoveStaff={onRemoveStaff} onReliefOnDoctor={onDropReliefSupervisor} getCandidates={getCandidates} noneMarked={doctorNone} hasMark={doctorHasMark} onSetMark={onSetDoctorMark} />
        <Slot plate={crna} kind="CRNA" needStaff={needCrna} noCred={crnaNoCred} scheduleId={scheduleId} onDropStaff={onDropStaff} onRemoveStaff={onRemoveStaff} onDoctorOnCrna={onDoctorOnCrna} getCandidates={getCandidates} noneMarked={crnaNone} hasMark={crnaHasMark} onSetMark={onSetCrnaMark} />
        <Slot plate={student} kind="student" scheduleId={scheduleId} onDropStaff={onDropStaff} onRemoveStaff={onRemoveStaff} getCandidates={getCandidates} />
      </div>
    </div>
  );
};

export default RoomNamePlates;
